import { useEffect, useRef, useState } from 'react';

//hooks
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

//components
import {
  Section,
  Kicker,
  KickerDot,
  SectionTitle,
} from '@/containers-ai/SectionHeading';
import { Container } from '@/components/Grid';

//styles
import {
  Stage,
  StageHead,
  StageHeadText,
  StageHeadTitle,
  StageHeadMeta,
  StagePill,
  Log,
  LogRow,
  LogTime,
  LogText,
  Summary,
  SummaryLabel,
  SummaryText,
  StageFoot,
  StageFootSource,
  StageFootRight,
  AgentBadge,
  ReplayButton,
  EscalationNote,
  EscalationTitle,
  EscalationText,
} from './WatchItRunYourFreight.styles';

const ROW_INTERVAL = 900;
const ROW_START_DELAY = 900;
const RESOLVE_DELAY = 5300;
const SUMMARY_DELAY = 5700;

export function WatchItRunYourFreight({
  title,
  stageHeadTitle,
  stageHeadMeta,
  rows,
  summaryText,
  agentBadge,
  escalationTitle,
  escalationText,
}) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const stageRef = useRef(null);
  const startedRef = useRef(false);
  const timersRef = useRef([]);
  const [activeRows, setActiveRows] = useState(new Set());
  const [done, setDone] = useState(false);
  const [summaryVisible, setSummaryVisible] = useState(false);

  function clearTimers() {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }

  function play() {
    clearTimers();
    setActiveRows(new Set());
    setDone(false);
    setSummaryVisible(false);

    if (prefersReducedMotion) {
      setActiveRows(new Set(rows.map((_, index) => index)));
      setDone(true);
      setSummaryVisible(true);
      return;
    }

    rows.forEach((_, index) => {
      timersRef.current.push(
        setTimeout(
          () => {
            setActiveRows((prev) => new Set([...prev, index]));
          },
          ROW_START_DELAY + index * ROW_INTERVAL
        )
      );
    });
    timersRef.current.push(setTimeout(() => setDone(true), RESOLVE_DELAY));
    timersRef.current.push(
      setTimeout(() => setSummaryVisible(true), SUMMARY_DELAY)
    );
  }

  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      play();
      return undefined;
    }
    const el = stageRef.current;
    if (!el) return undefined;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          play();
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefersReducedMotion]);

  return (
    <Section id="watch-it-run">
      <Container>
        <Kicker>
          <KickerDot />
          Watch it run your freight
        </Kicker>
        <SectionTitle style={{ maxWidth: 'none' }}>{title}</SectionTitle>

        <Stage ref={stageRef}>
          <StageHead>
            <StageHeadText>
              <StageHeadTitle>{stageHeadTitle}</StageHeadTitle>
              <StageHeadMeta>{stageHeadMeta}</StageHeadMeta>
            </StageHeadText>
            <StagePill $done={done}>{done ? 'Resolved' : 'Working'}</StagePill>
          </StageHead>

          <Log>
            {rows.map((row, index) => (
              <LogRow key={row.time} $active={activeRows.has(index)}>
                <LogTime>{row.time}</LogTime>
                <LogText $last={index === rows.length - 1}>{row.text}</LogText>
              </LogRow>
            ))}
          </Log>

          <Summary $active={summaryVisible}>
            <SummaryLabel>What just happened</SummaryLabel>
            <SummaryText>{summaryText}</SummaryText>
          </Summary>

          <StageFoot>
            <StageFootSource>
              Loadsmart AI &middot; scripted session
            </StageFootSource>
            <StageFootRight>
              <AgentBadge>{agentBadge}</AgentBadge>
              <ReplayButton type="button" onClick={play}>
                Replay &#8635;
              </ReplayButton>
            </StageFootRight>
          </StageFoot>
        </Stage>

        <EscalationNote>
          <EscalationTitle>{escalationTitle}</EscalationTitle>
          <EscalationText>{escalationText}</EscalationText>
        </EscalationNote>
      </Container>
    </Section>
  );
}
